import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { scrollIntoView } from '@theme/scrolling';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    const scrollGallery = this.querySelector('[data-scroll-gallery]');
    if (scrollGallery) {
      scrollGallery.addEventListener('scroll', this.#syncScrollGallery, { signal, passive: true });
      this.querySelector('.media-gallery__desktop-thumbnails')?.addEventListener('click', this.#handleThumbnailClick, {
        signal,
      });
    }
  }

  #controller = new AbortController();
  #scrollAnimationFrame = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    cancelAnimationFrame(this.#scrollAnimationFrame);
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
    if (window.matchMedia('(min-width: 1200px)').matches) this.#selectScrollGallery(event.detail.index, 'auto');
  };

  #handleThumbnailClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-scroll-gallery-index]');
    if (!button) return;
    this.#selectScrollGallery(Number(button.getAttribute('data-scroll-gallery-index')), 'smooth');
  };

  #selectScrollGallery(index, behavior) {
    const grid = this.querySelector('[data-scroll-gallery]');
    const media = grid?.children[index];
    if (!grid || !media) return;
    grid.scrollTo({
      top: grid.scrollTop + media.getBoundingClientRect().top - grid.getBoundingClientRect().top - 24,
      behavior,
    });
    this.#setCurrentThumbnail(index);
  }

  #syncScrollGallery = () => {
    if (this.#scrollAnimationFrame) return;
    this.#scrollAnimationFrame = requestAnimationFrame(() => {
      this.#scrollAnimationFrame = 0;
      const grid = this.querySelector('[data-scroll-gallery]');
      if (!grid) return;
      const top = grid.getBoundingClientRect().top + 24;
      const media = [...grid.children];
      const current = media.reduce(
        (best, item, index) =>
          Math.abs(item.getBoundingClientRect().top - top) < Math.abs(media[best].getBoundingClientRect().top - top)
            ? index
            : best,
        0,
      );
      this.#setCurrentThumbnail(current);
    });
  };

  #setCurrentThumbnail(index) {
    const thumbnails = this.querySelector('.media-gallery__desktop-thumbnails');
    const buttons = thumbnails?.querySelectorAll('[data-scroll-gallery-index]');
    const selected = buttons?.[index];
    if (!thumbnails || !selected || selected.getAttribute('aria-current') === 'true') return;
    buttons.forEach((button, buttonIndex) => {
      button.setAttribute('aria-current', String(buttonIndex === index));
    });
    scrollIntoView(selected, { ancestor: thumbnails, behavior: 'instant', block: 'center' });
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
